import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Alert, Loader } from '../common';

interface TokenVerificationFormProps {
  phone: string;
  expiresAt: string;
  onSuccess: () => void;
  onBack: () => void;
}

export const TokenVerificationForm: React.FC<TokenVerificationFormProps> = ({ 
  phone, 
  expiresAt, 
  onSuccess, 
  onBack 
}) => {
  const { verifyToken, error } = useAuth();
  const [token, setToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);

  // Calcul du temps restant avant expiration
  useEffect(() => {
    const calculateTimeLeft = () => {
      const expiresAtDate = new Date(expiresAt);
      const now = new Date();
      const difference = expiresAtDate.getTime() - now.getTime();
      return Math.max(0, Math.floor(difference / 1000));
    };

    setTimeLeft(calculateTimeLeft());
    
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
      
      if (newTimeLeft <= 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  // Formatage du temps restant en minutes:secondes
  const formatTimeLeft = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Limite à 10 caractères
    const value = e.target.value.slice(0, 10);
    setToken(value);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    // Validation basique
    if (!token) {
      setFormError('Veuillez entrer le code reçu par SMS');
      return;
    }

    try {
      setIsLoading(true);
      const response = await verifyToken(token);
      
      if (response.status === 'success' && response.data) {
        onSuccess();
      } else {
        setFormError('Code invalide ou expiré');
      }
    } catch (error) {
      if (error instanceof Error) {
        setFormError(error.message);
      } else {
        setFormError('Une erreur est survenue lors de la vérification du code');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg px-8 pt-6 pb-8 mb-4">
        <h2 className="text-2xl font-bold mb-6 text-center text-secondary">Vérification</h2>
        
        {(formError || error) && (
          <div className="mb-4">
            <Alert 
              type="error" 
              message={formError || error || ''} 
              onClose={() => setFormError(null)}
            />
          </div>
        )}
        
        <div className="mb-4">
          <Alert 
            type="info" 
            message={`Un code a été envoyé au ${phone}. Veuillez l'entrer ci-dessous.`}
            showIcon={true}
            dismissible={false}
          />
        </div>
        
        {timeLeft > 0 ? (
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="token">
              Code d'authentification
            </label>
            <input
              id="token"
              type="text"
              className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-secondary text-center text-xl tracking-widest"
              placeholder="XXXXXXXXXX"
              value={token}
              onChange={handleTokenChange}
              disabled={isLoading}
              autoComplete="one-time-code"
              maxLength={10}
            />
            <p className="text-sm text-gray-500 mt-1 text-center">
              Expire dans: <span className="font-medium">{formatTimeLeft()}</span>
            </p>
          </div>
        ) : (
          <div className="mb-6">
            <Alert 
              type="warning" 
              message="Le code a expiré. Veuillez demander un nouveau code."
              showIcon={true}
              dismissible={false}
            />
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <button
            type="button"
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            onClick={onBack}
            disabled={isLoading}
          >
            Retour
          </button>
          
          <button
            type="submit"
            className="bg-primary hover:bg-yellow-500 text-tertiary font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            disabled={isLoading || timeLeft <= 0}
          >
            {isLoading ? <Loader size="small" color="tertiary" /> : 'Vérifier'}
          </button>
        </div>
      </form>
    </div>
  );
};
